import { useEffect, useState } from "react"
import { Platform } from "react-native"

import * as VideoThumbnails from "expo-video-thumbnails"

import { delay } from "src/utils/delay"
import { storage } from "src/utils/storage/mmkv"
import { getYouTubeThumbnailUrl, getYouTubeVideoId } from "src/utils/youtubeUtils"

const MAX_RETRIES = 3
const RETRY_DELAYS = [1000, 2000, 3000] // Progressive delays: 1s, 2s, 3s
const THUMBNAIL_CACHE_PREFIX = "video_thumbnail:"

// Helper functions for persistent thumbnail cache
export const getCachedThumbnail = (videoUrl: string): string | undefined => {
  try {
    return storage.getString(`${THUMBNAIL_CACHE_PREFIX}${videoUrl}`)
  } catch (error) {
    console.error("[useVideoThumbnail] Error reading from cache:", error)
    return undefined
  }
}

export const setCachedThumbnail = (videoUrl: string, thumbnailUri: string): void => {
  try {
    storage.set(`${THUMBNAIL_CACHE_PREFIX}${videoUrl}`, thumbnailUri)
  } catch (error) {
    console.error("[useVideoThumbnail] Error writing to cache:", error)
  }
}

export const clearVideoThumbnailCache = (): void => {
  try {
    const allKeys = storage.getAllKeys()
    const thumbnailKeys = allKeys.filter((key) => key.startsWith(THUMBNAIL_CACHE_PREFIX))
    thumbnailKeys.forEach((key) => {
      storage.delete(key)
    })
    console.log(`[useVideoThumbnail] Cleared ${thumbnailKeys.length} cached thumbnails`)
  } catch (error) {
    console.error("[useVideoThumbnail] Error clearing cache:", error)
  }
}

/**
 * Check if a URL is an HLS stream (m3u8 playlist)
 */
export function isHlsUrl(url: string): boolean {
  return url.includes(".m3u8")
}

/**
 * Derive the original MP4 URL from an HLS URL.
 * Pattern: /files/hls/{id}/{id}.m3u8 → /files/{id}
 *
 * @param hlsUrl - The HLS m3u8 URL
 * @returns The derived MP4 URL, or undefined if pattern doesn't match
 */
export function deriveOriginalUrlFromHls(hlsUrl: string): string | undefined {
  // Match pattern: /files/hls/{id}/{id}.m3u8
  const hlsPattern = /\/files\/hls\/([a-f0-9-]+)\/[a-f0-9-]+\.m3u8/i
  const match = hlsUrl.match(hlsPattern)

  if (match && match[1]) {
    const id = match[1]
    // Replace the HLS path with the original file path
    const baseUrl = hlsUrl.split("/files/hls/")[0]
    const originalUrl = `${baseUrl}/files/${id}`
    console.debug("[useVideoThumbnail] Derived original URL from HLS:", originalUrl)
    return originalUrl
  }

  return undefined
}

/**
 * Web: capture a single frame from a video at the given time (Canvas API).
 * Used internally to generate library thumbnails.
 * Returns a data URL (image/jpeg).
 */
export function getVideoFrameAtTimeWeb(videoUrl: string, timeMs: number): Promise<string> {
  const timeSeconds = Math.max(0, timeMs / 1000)

  return new Promise((resolve, reject) => {
    const video = document.createElement("video")
    video.muted = true
    video.playsInline = true
    video.crossOrigin = "anonymous"
    video.preload = "metadata"

    const onError = () => {
      cleanup()
      reject(new Error("Video failed to load"))
    }

    const onLoadedData = () => {
      if (video.duration > 0) {
        video.currentTime = Math.min(timeSeconds, video.duration)
      } else {
        video.currentTime = timeSeconds
      }
    }

    const onSeeked = () => {
      try {
        const canvas = document.createElement("canvas")
        const w = video.videoWidth
        const h = video.videoHeight
        if (!w || !h) {
          cleanup()
          reject(new Error("Video dimensions not available"))
          return
        }
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext("2d")
        if (!ctx) {
          cleanup()
          reject(new Error("Canvas 2D not available"))
          return
        }
        ctx.drawImage(video, 0, 0, w, h)
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8)
        cleanup()
        resolve(dataUrl)
      } catch (err) {
        cleanup()
        reject(err)
      }
    }

    const cleanup = () => {
      video.removeEventListener("error", onError)
      video.removeEventListener("loadeddata", onLoadedData)
      video.removeEventListener("seeked", onSeeked)
      video.src = ""
      video.load()
    }

    video.addEventListener("error", onError)
    video.addEventListener("loadeddata", onLoadedData)
    video.addEventListener("seeked", onSeeked)

    video.src = videoUrl
    video.load()
  })
}

/** Web: thumbnail at 2s for list/card thumbnails (used by generateThumbnail). */
async function generateVideoThumbnailWeb(videoUrl: string): Promise<string> {
  return getVideoFrameAtTimeWeb(videoUrl, 2000)
}

// Generate a thumbnail for a video URL with retry logic
// Returns the thumbnail URI or throws if all attempts fail
export async function generateThumbnail(videoUrl: string): Promise<string> {
  const isYouTubeVideo = videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be")

  if (isYouTubeVideo) {
    const videoId = getYouTubeVideoId(videoUrl)
    if (videoId) {
      return getYouTubeThumbnailUrl(videoId)
    }
  }

  // If this is an HLS URL, try to derive the original MP4 URL for thumbnail generation
  let urlForThumbnail = videoUrl
  if (isHlsUrl(videoUrl)) {
    const originalUrl = deriveOriginalUrlFromHls(videoUrl)
    if (originalUrl) {
      console.debug("[useVideoThumbnail] Using derived MP4 URL for thumbnail:", originalUrl)
      urlForThumbnail = originalUrl
    } else {
      console.debug(
        "[useVideoThumbnail] Could not derive original URL from HLS, thumbnail generation not supported",
      )
      throw new Error("HLS streams are not supported for thumbnail generation")
    }
  }

  // Web: use Canvas API to capture a video frame (expo-video-thumbnails not supported on web)
  if (Platform.OS === "web") {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        return await generateVideoThumbnailWeb(urlForThumbnail)
      } catch (error) {
        const isLastAttempt = attempt === MAX_RETRIES
        if (isLastAttempt) {
          console.warn("[useVideoThumbnail] Web thumbnail failed after retries")
          throw error
        }
        await delay(RETRY_DELAYS[attempt])
      }
    }
    throw new Error("Web thumbnail generation failed after all retries")
  }

  // Native: expo-video-thumbnails
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const thumbnail: VideoThumbnails.VideoThumbnailsResult =
        await VideoThumbnails.getThumbnailAsync(urlForThumbnail, {
          time: 2000, // Extract frame at 2 seconds into the video
        })
      return thumbnail.uri
    } catch (error) {
      const isLastAttempt = attempt === MAX_RETRIES

      if (isLastAttempt) {
        console.warn("[useVideoThumbnail] Max retries reached, thumbnail generation failed")
        throw error
      }

      const delayMs = RETRY_DELAYS[attempt]
      console.warn(`[useVideoThumbnail] Attempt ${attempt + 1} failed, retrying in ${delayMs}ms...`)
      await delay(delayMs)
    }
  }

  throw new Error("Thumbnail generation failed after all retries")
}

// Hook interface similar to TanStack Query
export interface UseVideoThumbnailResult {
  thumbnailUri: string | undefined
  isLoading: boolean
  isError: boolean
  error: Error | undefined
}

/**
 * Hook to load and cache video thumbnails with automatic retry logic.
 * Returns a TanStack Query-like interface with status fields.
 *
 * @param videoUrl - The URL of the video to generate a thumbnail for
 * @returns Object containing thumbnailUri, isLoading, isError, and error
 *
 * @example
 * const { thumbnailUri, isLoading, isError } = useVideoThumbnail(videoUrl)
 */
export function useVideoThumbnail(videoUrl: string): UseVideoThumbnailResult {
  const [thumbnailUri, setThumbnailUri] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [error, setError] = useState<Error | undefined>(undefined)

  useEffect(() => {
    let cancelled = false

    async function loadThumbnail() {
      // Reset state when URL changes
      setIsLoading(true)
      setIsError(false)
      setError(undefined)

      // Check persistent cache first
      const cachedThumbnail = getCachedThumbnail(videoUrl)
      if (cachedThumbnail) {
        console.debug("[useVideoThumbnail] Using cached thumbnail from disk")
        if (!cancelled) {
          setThumbnailUri(cachedThumbnail)
          setIsLoading(false)
        }
        return
      }

      // Generate thumbnail
      try {
        console.debug("[useVideoThumbnail] Generating thumbnail for video URL:", videoUrl)
        const uri = await generateThumbnail(videoUrl)
        console.debug(
          "[useVideoThumbnail] Generated thumbnail for video URL:",
          videoUrl,
          "URI:",
          uri,
        )

        // Cache and update UI
        setCachedThumbnail(videoUrl, uri)
        if (!cancelled) {
          setThumbnailUri(uri)
          setIsLoading(false)
        }
      } catch (err) {
        console.warn("[useVideoThumbnail] Failed to generate thumbnail:", err)
        if (!cancelled) {
          setIsError(true)
          setError(err instanceof Error ? err : new Error(String(err)))
          setIsLoading(false)
        }
      }
    }

    loadThumbnail()

    // Cleanup: mark as cancelled if component unmounts
    return () => {
      cancelled = true
    }
  }, [videoUrl])

  return {
    thumbnailUri,
    isLoading,
    isError,
    error,
  }
}
